import * as React from "react";

// MUI
import {
    Box,
    Grid,
    Stack,
    Typography,
    Card,
    CardHeader,
    CardContent,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    Tooltip,
} from "@mui/material";
import { FaArrowTrendDown, FaArrowTrendUp, FaMinus } from 'react-icons/fa6';

// MUI X Charts
import { PieChart, pieArcLabelClasses } from "@mui/x-charts/PieChart";

// Your project card wrapper (kept from your codebase)
import MainCard from "../../../src/components/MainCard";
import { negativeSentimentColor, neutralSentimentColor, positiveSentimentColor } from "../../utils/constant";

/* --------------------------- Helpers --------------------------- */

const formatSignedPercent = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return "0%";
    return `${n > 0 ? "+" : ""}${n}%`;
};

const trendIconColor = (n) =>
    n > 0 ? "error" : n < 0 ? "success" : "default"; // ↑ red (worse), ↓ green (better)

const TrendChip = ({ value }) => {
    const n = Number(value);
    const label = formatSignedPercent(n);
    const color = trendIconColor(n);

    const icon =
        n > 0 ? <FaArrowTrendUp /> : n < 0 ? <FaArrowTrendDown /> : <FaMinus />;

    return (
        <Chip
            size="small"
            variant="outlined"
            color={color === "default" ? undefined : color}
            icon={icon}
            label={label}
        />
    );
};

/* ---------------- Recent Negative Keywords Card ---------------- */

export function RecentNegativeKeywords({
    data,
    title = "Recent Negative Keywords",
    limit = 5,
}) {
    const items = Array.isArray(data?.currentNegativeTop)
        ? data.currentNegativeTop.slice(0, limit)
        : [];

    return (
        <Card variant="outlined" sx={{ height: "100%" }}>
            <CardHeader
                title={title}
                subheader={
                    <Typography variant="body2" color="text.secondary">
                        Week-over-week change in negative mentions
                    </Typography>
                }
            />
            <CardContent sx={{ pt: 0 }}>
                {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No recent negative keywords found.
                    </Typography>
                ) : (
                    <List disablePadding>
                        {items.map((el, idx) => {
                            const key = `${el?.label ?? "item"}-${idx}`;
                            const percent = Number(el?.changePercentage ?? 0);
                            const count =
                                typeof el?.count === "number"
                                    ? `${el.count} mention${el.count === 1 ? "" : "s"}`
                                    : null;

                            return (
                                <React.Fragment key={key}>
                                    <ListItem
                                        disableGutters
                                        secondaryAction={
                                            <Tooltip
                                                title={
                                                    percent > 0
                                                        ? "Increase in negative mentions"
                                                        : percent < 0
                                                            ? "Decrease in negative mentions"
                                                            : "No change"
                                                }
                                                arrow
                                            >
                                                <Box>
                                                    <TrendChip value={percent} />
                                                </Box>
                                            </Tooltip>
                                        }
                                    >
                                        <ListItemText
                                            primary={el?.label ?? "—"}
                                            secondary={count}
                                            primaryTypographyProps={{ variant: "body1" }}
                                            secondaryTypographyProps={{
                                                variant: "caption",
                                                color: "text.secondary",
                                            }}
                                        />
                                    </ListItem>
                                    {idx < items.length - 1 && <Divider component="li" />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </CardContent>
        </Card>
    );
}

/* ----------------------- Sentiment Pie Chart ------------------- */

export function SentimentAnalyticPieChart({ pieSentimentAnalyticsData }) {

    const data = React.useMemo(() => {
        const raw = pieSentimentAnalyticsData?.result?.pieData;
        if (!Array.isArray(raw)) return [];
        return raw.map((item, idx) => ({
            id: item?.id ?? idx,
            label: item?.label ?? `Item ${idx + 1}`,
            // Slices sized by "value"; fallback to count if needed
            value: Number(item?.value ?? item?.count ?? 0),
            // Keep count for labels/tooltips
            count: Number(item?.count ?? 0),
        }));
    }, [pieSentimentAnalyticsData]);

    const TOTAL = React.useMemo(
        () => data.reduce((sum, d) => sum + (Number(d.value) || 0), 0),
        [data]
    );

    const getArcLabel = React.useCallback(
        // Show percent + count, e.g., "90% (9)"
        (params) => {
            if (TOTAL <= 0) return "0";
            const percent = Math.round((params.value / TOTAL) * 100);
            const cnt = data[params.dataIndex]?.count ?? 0;
            return `${percent}${cnt ? ` (${cnt})` : ""}`;
        },
        [TOTAL, data]
    );

    // Optional: consistent colors by sentiment
    const colorMap = {
        Positive: positiveSentimentColor, // green
        Neutral: neutralSentimentColor, // gray
        Negative: negativeSentimentColor, // red
    };
    const coloredData = data.map((d) => ({
        ...d,
        color: colorMap[d.label] ?? undefined,
    }));

    return (
        <>
            <Typography variant="h5">Sentiment Distribution</Typography>
            <MainCard sx={{ mt: 2 }}>
                <Stack
                    direction="row"
                    sx={{ alignItems: "center", justifyContent: "space-between" }}
                >
                    <Box>
                        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
                            Share of Positive / Neutral / Negative
                        </Typography>
                    </Box>
                </Stack>

                {data.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No data available for the selected range.
                    </Typography>
                ) : (
                    <PieChart
                        series={[
                            {
                                data: coloredData,
                                outerRadius: 150,
                                arcLabel: getArcLabel,
                            },
                        ]}
                        slotProps={{ legend: { hidden: true } }} // hide legend
                        sx={{
                            [`& .${pieArcLabelClasses.root}`]: {
                                fill: "white",
                                fontSize: 14,
                            },
                        }}
                        width={388}
                        height={525}
                    />
                )}
            </MainCard>
        </>
    );
}

/* ----------------------- Combined Section ---------------------- */

export default function SentimentOverview({
    pieSentimentAnalyticsData,
    negativeKeywordsData, // expects { currentNegativeTop: [{ label, changePercentage, count? }, ...] }
}) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <SentimentAnalyticPieChart
                    pieSentimentAnalyticsData={pieSentimentAnalyticsData}
                />
            </Grid>


        </Grid>
    );
}

